import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NudgeConversionCard — guest signup-nudge funnel for SuperAdmin. Reads
 * `GET /api/analytics/nudge/summary` (super_admin only) and shows impressions,
 * click-through rate, and a per-trigger breakdown (chapters / gated /
 * verse_action) so thresholds can be tuned with real numbers.
 */
const TRIGGER_META = {
  chapters:     { label: 'Read 3+ chapters', color: 'bg-amber-500' },
  gated:        { label: 'Tapped gated tool ×2', color: 'bg-orange-500' },
  verse_action: { label: 'Highlighted / copied a verse', color: 'bg-emerald-500' },
  unknown:      { label: 'Unknown', color: 'bg-slate-400' },
};

const NudgeConversionCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/analytics/nudge/summary`, {
        withCredentials: true,
      });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load nudge analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const byEvent = data?.by_event || {};
  const shown = byEvent.shown || 0;
  const clicked = byEvent.clicked || 0;
  const dismissed = byEvent.dismissed || 0;
  const ctr = data?.click_through_rate != null ? Math.round(data.click_through_rate * 100) : 0;
  const byTrigger = data?.by_trigger || {};

  return (
    <Card className="border-2 border-amber-300/50 dark:border-amber-700/40" data-testid="nudge-conversion-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <TrendingUp className="w-5 h-5" />
            Guest Signup Nudge — Conversion
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="nudge-conversion-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !data && (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
          </div>
        )}
        {error && <p className="text-red-600 italic" data-testid="nudge-conversion-error">{error}</p>}

        {data && shown === 0 && (
          <p className="text-slate-500 italic" data-testid="nudge-conversion-empty">
            No nudge impressions recorded yet. Once guests engage (read chapters,
            tap gated tools, or highlight/copy verses), the funnel will populate here.
          </p>
        )}

        {data && shown > 0 && (
          <>
            {/* Headline metrics */}
            <div className="grid grid-cols-3 gap-3" data-testid="nudge-conversion-metrics">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold">{shown}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Shown</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{clicked}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Clicked</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{ctr}%</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Click-through</p>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              {dismissed} dismissed · {data.total} total events
            </p>

            {/* Per-trigger breakdown */}
            <div className="space-y-2" data-testid="nudge-conversion-triggers">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">
                Which trigger converts best (clicks ÷ shown)
              </p>
              {Object.keys(TRIGGER_META).map((trig) => {
                const t = byTrigger[trig];
                if (!t) return null;
                const tShown = t.shown || 0;
                const tClicked = t.clicked || 0;
                const pct = tShown > 0 ? Math.round((tClicked / tShown) * 100) : 0;
                const meta = TRIGGER_META[trig];
                return (
                  <div key={trig} className="space-y-1" data-testid={`nudge-trigger-${trig}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{meta.label}</span>
                      <span className="text-xs text-slate-500">
                        {tClicked}/{tShown} · {pct}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full ${meta.color} transition-all`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default NudgeConversionCard;
